import { memo, useCallback, useEffect, useState, useContext, useMemo, type FunctionComponent } from 'react';
import size from 'lodash/size';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import includes from 'lodash/includes';
import { useLazyQuery } from '@apollo/client';
// Material UI imports
import { type SelectChangeEvent } from '@mui/material/Select';
import Box from '@mui/material/Box';
// Skillmore UI Components
import { pathBuilder } from '@empathco/ui-components/src/helpers/graphql';
import { POLLING_FETCH_POLICY } from '@empathco/ui-components/src/hooks/usePolling';
import useQueryObject from '@empathco/ui-components/src/hooks/useQueryObject';
import OnOffSwitch from '@empathco/ui-components/src/elements/OnOffSwitch';
// local imports
import { CONST_JOBS, toValidConst } from '../constants/constValues';
import { DA_SKILLS_INSIGHTS_QUERY } from '../graphql/DASkillsInsights';
import { DA_JOBS_MOVEMENT_QUERY } from '../graphql/DAJobsMovement';
import { DA_HIRING_TRENDS_QUERY } from '../graphql/DAHiringTrends';
import { DA_JOB_RELATED_SKILLS_QUERY } from '../graphql/DAJobRelatedSkills';
import {
  JobInDemand, JobsMovementOrder, SkillType,
  DASkillsInsightsDocument, DASkillsInsightsQuery,
  DAJobsMovementDocument, DAJobsMovementQuery,
  DAHiringTrendsDocument, DAHiringTrendsQuery,
  DAJobRelatedSkillsDocument, DAJobRelatedSkillsQuery
} from '../graphql/types';
import { JOBS_MOVEMENT_ORDER, JOB_RELATED_SKILLS_ORDERS } from '../graphql/customTypes';
import {
  DA_FREQ_SKILL_GAP, DA_HIRING_TRENDS, DA_JOBS_MOVEMENT, DA_JOB_RELATED_SKILLS
} from '../constants/dashboardAnalytics';
import useCustomerSettings from '../config/customer';
import {
  API_DA_HIRING_TRENDS_EXPORT, API_DA_JOBS_MOVEMENT_EXPORT, API_DA_JOB_RELATED_SKILLS_EXPORT,
  API_DA_SKILLS_INSIGHTS_EXPORT
} from '../config/api';
import { PATH_JOB, PATH_SKILL } from '../config/paths';
import { omitParamsBySkillType } from '../helpers/analytics';
import { getScopeFetchMessage, isScopeRefused } from '../helpers/graphql';
import { FilterValues } from '../hooks/useFilters';
import useChartPolling from '../hooks/useChartStatus';
import { GlobalContext } from '../context/global';
import ConstSelector from '../elements/ConstSelector';
import SortSelector from '../elements/SortSelector';
import TopChart, { TopChartProps } from '../widgets/TopChart';
// SCSS imports
import { durationSelector } from './DashboardAnalyticsJobs.module.scss';

type DashboardAnalyticsJobsProps = {
  readonly chartId: number;
  readonly skillType: SkillType;
  readonly filters?: FilterValues;
  readonly years: number;
  readonly pending?: boolean | null;
  readonly disabled?: boolean | null;
  readonly onEmployeeCountChange?: (value: number | null | undefined) => void;
  readonly onScopeRefused?: () => void;
  readonly pinned?: number[];
  readonly onPin: (value: number) => void;
  readonly pinning?: boolean;
  readonly testCase?: number;
}

const DashboardAnalyticsJobs: FunctionComponent<DashboardAnalyticsJobsProps> = ({
  chartId,
  skillType,
  filters,
  years,
  pending: parentPending = false,
  disabled = false,
  onEmployeeCountChange,
  onScopeRefused,
  pinned,
  onPin,
  pinning = false,
  testCase
  // eslint-disable-next-line complexity, max-lines-per-function, max-statements
}) => {
  const { DA_TABS, HAS_INDEMAND_SKILLS } = useCustomerSettings();
  const { fontsLoaded, token } = useContext(GlobalContext);
  const pending = parentPending || !fontsLoaded;

  // lazy load Skills Insights data
  const {
    query: getSkillsInsights, pending: pendingSkillsInsights, failed: failedSkillsInsights, results: skillsInsights,
    error: errorSkillsInsights
  } = useQueryObject({
    data: undefined as unknown as DASkillsInsightsQuery['daSkillsInsights'],
    key: 'daSkillsInsights',
    flatResults: true,
    lazyQuery: useLazyQuery(DA_SKILLS_INSIGHTS_QUERY as typeof DASkillsInsightsDocument)
  });
  const pollingSkillsInsights =
    useChartPolling(chartId === DA_FREQ_SKILL_GAP, failedSkillsInsights, skillsInsights?.status);

  // lazy load Hiring Trends data
  const {
    query: getHiringTrends, pending: pendingHiringTrends, failed: failedHiringTrends, results: hiringTrends,
    error: errorHiringTrends
  } = useQueryObject({
    data: undefined as unknown as DAHiringTrendsQuery['daHiringTrends'],
    key: 'daHiringTrends',
    flatResults: true,
    lazyQuery: useLazyQuery(DA_HIRING_TRENDS_QUERY as typeof DAHiringTrendsDocument)
  });
  const pollingHiringTrends =
    useChartPolling(chartId === DA_HIRING_TRENDS, failedHiringTrends, hiringTrends?.status);

  // lazy load Jobs Movement data
  const {
    query: getJobsMovement, pending: pendingJobsMovement, failed: failedJobsMovement, results: jobsMovement,
    error: errorJobsMovement
  } = useQueryObject({
    data: undefined as unknown as DAJobsMovementQuery['daJobsMovement'],
    key: 'daJobsMovement',
    flatResults: true,
    lazyQuery: useLazyQuery(DA_JOBS_MOVEMENT_QUERY as typeof DAJobsMovementDocument)
  });
  const pollingJobsMovement =
    useChartPolling(chartId === DA_JOBS_MOVEMENT, failedJobsMovement, jobsMovement?.status);

  // lazy load Jobs Movement data
  const {
    query: getJobSkills, pending: pendingJobSkills, failed: failedJobSkills, results: jobSkills,
    error: errorJobSkills
  } = useQueryObject({
    data: undefined as unknown as DAJobRelatedSkillsQuery['daJobRelatedSkills'],
    key: 'daJobRelatedSkills',
    flatResults: true,
    lazyQuery: useLazyQuery(DA_JOB_RELATED_SKILLS_QUERY as typeof DAJobRelatedSkillsDocument)
  });
  const pollingJobSkills =
    useChartPolling(chartId === DA_JOB_RELATED_SKILLS, failedJobSkills, jobSkills?.status);

  // Every chart here carries the same `manager_id`, so any of them can be the one refused for it,
  // and the browser owns the selector that produced it.
  const scopeRefused = isScopeRefused(errorSkillsInsights, errorHiringTrends, errorJobsMovement, errorJobSkills);
  useEffect(() => {
    if (scopeRefused) onScopeRefused?.();
  }, [scopeRefused, onScopeRefused]);

  // Skills Insights parameters:
  // const [transfersOnly, setTransfersOnly] = useState(false);
  const [confirmedOnly, setConfirmedOnly] = useState(testCase === 1);
  // Hiring Trends parameters:
  const [jobType, setJobType] = useState(JOBS_MOVEMENT_ORDER[1]);
  // Jobs Movement parameters:
  const [jobOrder, setJobOrder] = useState(JOBS_MOVEMENT_ORDER[0]);
  const [jobTotal, setJobTotal] = useState<number>(CONST_JOBS[1]);
  // Job Related Skills parameters:
  const [jobSkillsOrder, setJobSkillsOrder] = useState(JOB_RELATED_SKILLS_ORDERS[0]);
  const [jobSkillsTotal, setJobSkillsTotal] = useState<number>(CONST_JOBS[1]);

  const handleJobTotalChange = useCallback((event: SelectChangeEvent<number>) =>
    setJobTotal(toValidConst(event?.target?.value, CONST_JOBS[1], CONST_JOBS)), []);
  const handleJobSkillsTotalChange = useCallback((event: SelectChangeEvent<number>) =>
    setJobSkillsTotal(toValidConst(event?.target?.value, CONST_JOBS[1], CONST_JOBS)), []);

  useEffect(() => {
    onEmployeeCountChange?.({
      [DA_FREQ_SKILL_GAP]: !failedSkillsInsights && isNil(skillsInsights?.total_employees)
        ? null : skillsInsights?.total_employees,
      [DA_HIRING_TRENDS]: !failedHiringTrends && isNil(hiringTrends?.total_employees) ? null : hiringTrends?.total_employees,
      [DA_JOBS_MOVEMENT]: !failedJobsMovement && isNil(jobsMovement?.total_employees) ? null : jobsMovement?.total_employees,
      [DA_JOB_RELATED_SKILLS]: !failedJobSkills && isNil(jobSkills?.total_employees) ? null : jobSkills?.total_employees
    }[chartId]);
  }, [
    chartId,
    skillsInsights?.total_employees, failedSkillsInsights,
    hiringTrends?.total_employees, failedHiringTrends,
    jobsMovement?.total_employees, failedJobsMovement,
    jobSkills?.total_employees, failedJobSkills,
    onEmployeeCountChange
  ]);

  const [
    skillsInsightsExportParams,
    hiringTrendsExportParams,
    jobsMovementExportParams,
    jobSkillsExportParams
  ] = useMemo(() => {
    if (!filters || !token || !isString(token)) return [null, null, null, null];
    const params: FilterValues & { token: string; } = { ...filters, token };
    return [
      // Skills Insights
      { ...omitParamsBySkillType(params, skillType),
        // ...transfersOnly ? { transfers_only: true } : {},
        ...confirmedOnly ? { confirmed_only: true } : {},
        duration: 12 * years },
      // Hiring Trends
      { ...params,
        duration: 12 * years, ...jobType === JobsMovementOrder.target_count ? {} : { job_type: JobInDemand.most_indemand } },
      // Jobs Movement
      { ...params, job_order: jobOrder, job_total: jobTotal, duration: 12 * years },
      // Job Related Skills
      { ...params, job_order: jobSkillsOrder, job_total: jobSkillsTotal }
    ];
  }, [
    jobType, jobOrder, jobTotal, /* transfersOnly, */ confirmedOnly, skillType,
    jobSkillsOrder, jobSkillsTotal, years, filters, token
  ]);

  // load Skills Insights
  useEffect(() => {
    if (chartId === DA_FREQ_SKILL_GAP && DA_TABS?.frequent_skill_gap && filters && pollingSkillsInsights >= 0)
      getSkillsInsights?.({
        variables: {
          input: {
            ...omitParamsBySkillType(filters, skillType),
            // ...transfersOnly ? { transfers_only: true } : {},
            ...confirmedOnly ? { confirmed_only: true } : {},
            duration: 12 * years
          },
          pathBuilder
        },
        ...pollingSkillsInsights >= 1 ? POLLING_FETCH_POLICY : {}
      });
  }, [
    chartId, filters, skillType, /* transfersOnly, */ confirmedOnly, years, pollingSkillsInsights, getSkillsInsights,
    DA_TABS?.frequent_skill_gap
  ]);

  // load Hiring Trends
  useEffect(() => {
    if (
      chartId === DA_HIRING_TRENDS && (DA_TABS?.hiring_trends_all || DA_TABS?.hiring_trends_internal) && filters &&
      pollingHiringTrends >= 0
    ) getHiringTrends?.({
      variables: {
        input: {
          ...filters,
          duration: 12 * years,
          job_type: jobType === JobsMovementOrder.target_count ? null : JobInDemand.most_indemand
        },
        pathBuilder
      },
      ...pollingHiringTrends >= 1 ? POLLING_FETCH_POLICY : {}
    });
  }, [
    chartId, filters, jobType, years, getHiringTrends, pollingHiringTrends,
    DA_TABS?.hiring_trends_all, DA_TABS?.hiring_trends_internal
  ]);

  // load Jobs Movement counts
  useEffect(() => {
    if (
      chartId === DA_JOBS_MOVEMENT && HAS_INDEMAND_SKILLS && DA_TABS?.jobs_movement && filters && jobOrder && jobTotal &&
      pollingJobsMovement >= 0
    ) getJobsMovement?.({
      variables: {
        input: {
          ...filters,
          job_order: jobOrder,
          job_total: jobTotal,
          duration: 12 * years
        },
        pathBuilder
      },
      ...pollingJobsMovement >= 1 ? POLLING_FETCH_POLICY : {}
    });
  }, [
    chartId, filters, jobOrder, jobTotal, years, pollingJobsMovement, getJobsMovement,
    HAS_INDEMAND_SKILLS, DA_TABS?.jobs_movement
  ]);

  // load Job Related Skills counts
  useEffect(() => {
    if (
      chartId === DA_JOB_RELATED_SKILLS && DA_TABS?.job_related_skills && filters && jobSkillsOrder && jobSkillsTotal &&
      pollingJobSkills >= 0
    ) getJobSkills?.({
      variables: {
        input: {
          ...filters,
          job_order: jobSkillsOrder,
          job_total: jobSkillsTotal
        },
        pathBuilder
      },
      ...pollingJobSkills >= 1 ? POLLING_FETCH_POLICY : {}
    });
  }, [chartId, filters, jobSkillsOrder, jobSkillsTotal, pollingJobSkills, getJobSkills, DA_TABS?.job_related_skills]);

  const optionalParams = useMemo(() => ({ years }), [years]);

  const handlePinSkillsInsights = useCallback(() => onPin(DA_FREQ_SKILL_GAP), [onPin]);
  const handlePinHiringTrends = useCallback(() => onPin(DA_HIRING_TRENDS), [onPin]);
  const handlePinJobsMovement = useCallback(() => onPin(DA_JOBS_MOVEMENT), [onPin]);
  const handlePinJobSkills = useCallback(() => onPin(DA_JOB_RELATED_SKILLS), [onPin]);

  const hiringOptionalParams: TopChartProps['optionalParams'] = useMemo(() => ({
    internalHiresOnly: !DA_TABS?.hiring_trends_all && DA_TABS?.hiring_trends_internal
  }), [DA_TABS]);

  const exportDisabled = disabled || pending || !filters;

  return (
    <>
      {chartId === DA_FREQ_SKILL_GAP && DA_TABS?.frequent_skill_gap ? (
        <TopChart
            variant="frequent_skill_gap"
            hrbp
            layout="full"
            info="hr.dashboard.frequent_skill_gap.info"
            withInfoButton
            data={skillsInsights?.results}
            totalEmployees={skillsInsights?.total_employees}
            generating={pollingSkillsInsights >= 1}
            pending={pendingSkillsInsights || pending}
            failed={failedSkillsInsights}
            failureMessage={getScopeFetchMessage(errorSkillsInsights)}
            path={PATH_SKILL}
            action={(
              // TODO: enable when backend is ready:
              // <OnOffSwitch
              //     label="hr.dashboard.transfers_only"
              //     value={transfersOnly}
              //     onChange={setTransfersOnly}
              //     disabled // ={disabled || pollingSkillsInsights >= 1 || pendingSkillsInsights ? true : undefined}
              // />
              <OnOffSwitch
                  label="hr.dashboard.confirmed_skills_only"
                  italic
                  value={confirmedOnly}
                  onChange={setConfirmedOnly}
                  disabled={disabled || pollingSkillsInsights >= 1 || pendingSkillsInsights ? true : undefined}
              />
            )}
            pinned={includes(pinned, DA_FREQ_SKILL_GAP)}
            onPin={disabled ? undefined : handlePinSkillsInsights}
            pinning={pinning}
            exportEndpoint={API_DA_SKILLS_INSIGHTS_EXPORT}
            exportParams={skillsInsightsExportParams}
            exportDisabled={exportDisabled || pollingSkillsInsights >= 1 || pendingSkillsInsights || failedSkillsInsights ||
              size(skillsInsights?.results) < 1}
        />
      ) : undefined}
      {chartId === DA_HIRING_TRENDS && (DA_TABS?.hiring_trends_all || DA_TABS?.hiring_trends_internal) ? (
        <TopChart
            variant="hiring_trends"
            hrbp
            layout="full"
            info="hr.dashboard.hiring_trends.info"
            withInfoButton
            data={hiringTrends?.jobs}
            totalEmployees={hiringTrends?.total_employees}
            generating={pollingHiringTrends >= 1}
            pending={pendingHiringTrends || pending}
            failed={failedHiringTrends}
            failureMessage={getScopeFetchMessage(errorHiringTrends)}
            path={PATH_JOB}
            optionalParams={hiringOptionalParams}
            action={(
              <SortSelector
                  variant="jobs_order"
                  value={jobType}
                  onChange={setJobType}
                  disabled={disabled || pollingHiringTrends >= 1 || pendingHiringTrends ? true : undefined}
                  values={JOBS_MOVEMENT_ORDER}
              />
            )}
            pinned={includes(pinned, DA_HIRING_TRENDS)}
            onPin={disabled ? undefined : handlePinHiringTrends}
            pinning={pinning}
            exportEndpoint={API_DA_HIRING_TRENDS_EXPORT}
            exportParams={hiringTrendsExportParams}
            exportDisabled={exportDisabled || pollingHiringTrends >= 1 || pendingHiringTrends || failedHiringTrends ||
              size(hiringTrends?.jobs) < 1}
        />
      ) : undefined}
      {chartId === DA_JOBS_MOVEMENT && HAS_INDEMAND_SKILLS && DA_TABS?.jobs_movement ? (
        <TopChart
            variant="jobs_movement"
            hrbp
            layout="full"
            info="hr.dashboard.jobs_movement.info"
            withInfoButton
            data={jobsMovement?.jobs}
            totalEmployees={jobsMovement?.total_employees}
            generating={pollingJobsMovement >= 1}
            pending={pendingJobsMovement || pending}
            failed={failedJobsMovement}
            failureMessage={getScopeFetchMessage(errorJobsMovement)}
            path={PATH_JOB}
            action={(
              <>
                <Box py={1}>
                  <SortSelector
                      variant="jobs_order"
                      value={jobOrder}
                      onChange={setJobOrder}
                      disabled={disabled || pollingJobsMovement >= 1 || pendingJobsMovement ? true : undefined}
                      values={JOBS_MOVEMENT_ORDER}
                  />
                </Box>
                <Box py={1}>
                  <ConstSelector
                      variant="jobs"
                      value={jobTotal}
                      onChange={handleJobTotalChange}
                      disabled={disabled || pollingJobsMovement >= 1 || pendingJobsMovement ? true : undefined}
                      className={durationSelector}
                  />
                </Box>
              </>
            )}
            pinned={includes(pinned, DA_JOBS_MOVEMENT)}
            onPin={disabled ? undefined : handlePinJobsMovement}
            pinning={pinning}
            exportEndpoint={API_DA_JOBS_MOVEMENT_EXPORT}
            exportParams={jobsMovementExportParams}
            exportDisabled={exportDisabled || pollingJobsMovement >= 1 || pendingJobsMovement || failedJobsMovement ||
              size(jobsMovement?.jobs) < 1}
        />
      ) : undefined}
      {chartId === DA_JOB_RELATED_SKILLS && DA_TABS?.job_related_skills ? (
        <TopChart
            variant="job_skills"
            hrbp
            layout="full"
            info="hr.dashboard.job_skills.info"
            withInfoButton
            data={jobSkills?.results}
            totalEmployees={jobSkills?.total_employees}
            generating={pollingJobSkills >= 1}
            pending={pendingJobSkills || pending}
            failed={failedJobSkills}
            failureMessage={getScopeFetchMessage(errorJobSkills)}
            optionalParams={optionalParams}
            path={PATH_JOB}
            action={(
              <>
                <Box py={1}>
                  <SortSelector
                      variant="job_skills_order"
                      value={jobSkillsOrder}
                      onChange={setJobSkillsOrder}
                      disabled={disabled || pollingJobSkills >= 1 || pendingJobSkills ? true : undefined}
                      values={JOB_RELATED_SKILLS_ORDERS}
                  />
                </Box>
                <Box py={1}>
                  <ConstSelector
                      variant="jobs"
                      value={jobSkillsTotal}
                      onChange={handleJobSkillsTotalChange}
                      disabled={disabled || pollingJobSkills >= 1 || pendingJobSkills ? true : undefined}
                      className={durationSelector}
                  />
                </Box>
              </>
            )}
            pinned={includes(pinned, DA_JOB_RELATED_SKILLS)}
            onPin={disabled ? undefined : handlePinJobSkills}
            pinning={pinning}
            exportEndpoint={API_DA_JOB_RELATED_SKILLS_EXPORT}
            exportParams={jobSkillsExportParams}
            exportDisabled={exportDisabled || pollingJobSkills >= 1 || pendingJobSkills || failedJobSkills ||
              size(jobSkills?.results) < 1}
        />
      ) : undefined}
    </>
  );
};

export default memo(DashboardAnalyticsJobs);
